import ddLogger from '../../logging/ddLogger';
import { AdjustmentErrors } from '../../types/errors.type';
import { AddNightPreviewRequest, AdjustmentPreviewRequest, Reservation, TripAdjustmentResponse, Unit } from 'guest-data-types-ts-lib';
import { TripService } from '../../services/trip/tripService';
import _ from 'lodash';

export const getErrorAddNighAdjustment = (
    isAddNightTripEnabled: boolean,
    reservation: Reservation,
    adjustmentName?: string,
    excludedSourcesIds?: { id: number; name: string }
): AdjustmentErrors | null => {
    if (!reservation.vacasa_is_merchant_of_record || excludedSourcesIds) {
        ddLogger.info(`Reservation is not MOR or, a excluded source , not eligible for adjustment ${adjustmentName}`, {
            reservation_id: reservation.id,
            source_id: reservation.reservation_source_id,
        });
        return AdjustmentErrors.RESERVATION_SOURCE_EXCLUDED;
    }

    if (!isAddNightTripEnabled && reservation.balance > 0) {
        ddLogger.info(`Reservation has a balance, not eligible for adjustment ${adjustmentName}`, {
            reservation_id: reservation.id,
            balance: reservation.balance,
        });
        return AdjustmentErrors.PENDING_BALANCE;
    }

    return null;
};

export const getPublicAddNightPreview = async (
    tripService: TripService,
    isTripAddNightEnable: boolean,
    reservation: Reservation,
    adjustment: AdjustmentPreviewRequest
): Promise<{ discountedResponse: TripAdjustmentResponse; discountDetails: any; trackingExtras: any; regularResponse: any }> => {
    const { id: reservationId } = reservation;

    const regularResponse = (await tripService.getAdjustmentPreview(reservationId, {
        name: adjustment.name,
        data: { ...(adjustment as AddNightPreviewRequest).data, vxuser: undefined, discount_coupon: undefined },
    } as AddNightPreviewRequest)) as { attributes: TripAdjustmentResponse };

    const { attributes: discountedResponse } = (await tripService.getAdjustmentPreview(reservationId, adjustment)) as {
        type: string;
        attributes: TripAdjustmentResponse;
    };

    const discountAmount = regularResponse.attributes.delta.total - discountedResponse.delta.total;
    const discountAmountRent = regularResponse.attributes.delta.rent - discountedResponse.delta.rent;
    const originalRent = regularResponse.attributes.delta.rent;
    const discountDetails = {
        adjustment_date: (adjustment as any).data.date, // TODO: fix typing
        original_rent: originalRent,
        discount_amount: discountAmountRent,
    };

    const newReservationTotal = reservation ? discountedResponse.delta.total + reservation.total : undefined;

    const trackingExtras = {
        discount_amount: discountAmount,
        new_reservation_total: newReservationTotal,
    };

    return { discountedResponse, discountDetails, trackingExtras, regularResponse };
};

export const getTripAddNightPreview = async (
    tripService: TripService,
    isTripAddNightEnable: boolean,
    reservation: Reservation,
    adjustment: AdjustmentPreviewRequest
): Promise<{ adjustmentPreview: TripAdjustmentResponse; discountDetails: any; trackingExtras: any }> => {
    const { id: reservationId } = reservation;
    const { attributes: adjustmentPreview } = (await tripService.getAdjustmentPreview(reservationId, adjustment)) as {
        type: string;
        attributes: TripAdjustmentResponse;
    };

    const adjustmentDetail = adjustmentPreview.adjustment as {
        name: 'tripAddNight';
        data: {
            original_rent: number;
            discounted_rent: number;
            discounted_amount: number;
            first_night: string;
            last_night: string;
        };
    };

    const discountAmount = adjustmentDetail.data.discounted_amount;
    const discountAmountRent = adjustmentDetail.data.discounted_rent;
    const originalRent = adjustmentDetail.data.original_rent;
    const discountDetails = {
        adjustment_date: adjustmentDetail.data.last_night, // TODO: fix typing
        original_rent: originalRent,
        discount_amount: discountAmountRent,
        discount_rent: originalRent - discountAmountRent,
    };

    const newReservationTotal = reservation ? adjustmentPreview.delta.total + reservation.total : undefined;

    const trackingExtras = {
        discount_amount: discountAmount,
        new_reservation_total: newReservationTotal,
    };

    return { adjustmentPreview, discountDetails, trackingExtras };
};

export const getLateCheckoutPreview = async (
    tripService: TripService,
    reservation: Reservation,
    adjustment: AdjustmentPreviewRequest
): Promise<{ adjustmentPreview: TripAdjustmentResponse; trackingExtras: any }> => {
    const { id: reservationId } = reservation;
    const { attributes: adjustmentPreview } = (await tripService.getAdjustmentPreview(reservationId, adjustment)) as {
        type: string;
        attributes: TripAdjustmentResponse;
    };

    const adjustmentDetail = adjustmentPreview.adjustment as {
        name: 'lateCheckout';
        data: {
            check_in_date_time: string;
            check_out_date_time: string;
        };
    };

    const trackingExtras = {
        check_in_date_time: adjustmentDetail.data.check_in_date_time,
        check_out_date_time: adjustmentDetail.data.check_out_date_time,
    };

    return { adjustmentPreview, trackingExtras };
};

export const unitSupportsLateCheckout = (rawUnit: Unit): boolean => {
    const excludeLateCheckout = _.toNumber(rawUnit?.late_checkout_offer_exclude);
    return excludeLateCheckout < 1;
};

export const getPoolHeatPreview = async (
    tripService: TripService,
    reservation: Reservation,
    adjustment: AdjustmentPreviewRequest
): Promise<TripAdjustmentResponse> => {
    const { id: reservationId } = reservation;
    const { attributes: adjustmentPreview } = (await tripService.getAdjustmentPreview(reservationId, adjustment)) as {
        type: string;
        attributes: TripAdjustmentResponse;
    };

    return adjustmentPreview;
};
